import { useMemo } from "react";
import { useGetAllPortfoliosHoldings } from "api/holdings/useGetAllPortfoliosHoldings";
import { useGetSecurities } from "api/securities/useGetSecurities";
import { HOLDINGS_SETTINGS, LogoOption, VALID_ASSET_TYPES } from "../types";
import { DirectoryEntry, useIssuerDirectory } from "./useIssuerDirectory";

interface UseRelevantIssuersProps {
  holdingsSetting: HOLDINGS_SETTINGS;
  assetTypeSetting: VALID_ASSET_TYPES | null;
}

/** Asset type code -> the portal-side issuer ids found under it. */
type IdsByAssetType = Map<string, Set<string>>;

/**
 * Buckets grow to fit whatever FA sends. This used to be pre-seeded from
 * `VALID_ASSET_TYPES` and gated with `map.has(code)`, which made the enum an
 * allowlist: any security whose sector code was not one of the six literals
 * was dropped from *every* bucket, the unfiltered one included. That turned a
 * single wrong constant (`3ALTINV` for what FA actually calls `ALTER`) into a
 * silently empty picker rather than a visible error, and it would have hidden
 * `CASH` funds too. Relevance is decided by the CMS join further down, so
 * there is nothing for a code-level allowlist to protect here.
 */
const addIssuerId = (map: IdsByAssetType, code: string, issuerId: string) => {
  const bucket = map.get(code);
  if (bucket) bucket.add(issuerId);
  else map.set(code, new Set([issuerId]));
};

/**
 * Which issuers are worth contacting, given what the user holds (or the whole
 * fund universe) and an asset-type filter.
 *
 * This hook owns *relevance* only: it walks portal holdings/securities to
 * collect issuer ids per asset type, then inner-joins those ids against
 * `useIssuerDirectory` for name, logo and contactability. Consequences of that
 * split:
 *
 * - Identity (name, logo) comes from one swappable place, never from the
 *   portal's `class2Name` or a hardcoded logo map.
 * - Relevance and contactability are separate answers. Everything relevant is
 *   returned, contactable or not; `canBeContacted` rides along so the picker
 *   can show a fund company it cannot mail rather than pretend it does not
 *   exist. An issuer the directory does not list at all is still not offered —
 *   we have no name or logo for it.
 *
 * The FA join is deliberately NOT applied to unfiltered "Alla fondbolag":
 * plenty of contactable fund companies have no funds in FA's universe at all
 * (Mandatum, for one), and an inner join silently hid every one of them from
 * a list whose label promises all of them. See the note on the branch below.
 */
export const useRelevantIssuers = ({
  holdingsSetting,
  assetTypeSetting,
}: UseRelevantIssuersProps) => {
  const {
    data: portfolioHoldingsData,
    loading: portfolioHoldingsLoading,
    error: portfolioHoldingsError,
  } = useGetAllPortfoliosHoldings();

  const {
    data: allSecuritiesData,
    error: allSecuritiesError,
    loading: allSecuritiesLoading,
  } = useGetSecurities();

  const {
    data: directory,
    loading: directoryLoading,
    error: directoryError,
  } = useIssuerDirectory();

  const idsByAssetType = useMemo<IdsByAssetType>(() => {
    const map: IdsByAssetType = new Map();

    if (holdingsSetting === HOLDINGS_SETTINGS.PORTFOLIO) {
      // Holdings analytics only carry `class2Code`, i.e. FA's legacy
      // classType2 issuer, which is null for most funds now that FA keeps the
      // fund company in `security.issuer`. Resolve the issuer through the
      // securities list instead (which prefers `issuer` over classType2), and
      // fall back to `class2Code` only for a holding outside that list.
      const issuerIdBySecurityId = new Map<number, string>();
      allSecuritiesData?.forEach((security) => {
        if (security.issuer?.id != null)
          issuerIdBySecurityId.set(security.id, String(security.issuer.id));
      });

      portfolioHoldingsData?.allocationByType
        .flatMap((allocation) => allocation.allocationsBySector)
        .forEach((allocation) => {
          allocation.allocationsBySecurity.forEach(({ security }) => {
            const issuerId =
              issuerIdBySecurityId.get(security.id) ??
              (security.class2Code ? String(security.class2Code) : null);
            if (issuerId) addIssuerId(map, allocation.code, issuerId);
          });
        });
    } else if (holdingsSetting === HOLDINGS_SETTINGS.ALL) {
      allSecuritiesData?.forEach((security) => {
        if (!security.issuer) return;
        // A mixed fund sits under several asset types at once, so it makes its
        // issuer contactable about each of them — not just the first one FA
        // happens to list.
        security.sectorCodes.forEach((code) =>
          addIssuerId(map, code, String(security.issuer.id))
        );
      });
    }

    return map;
  }, [holdingsSetting, portfolioHoldingsData, allSecuritiesData]);

  const data = useMemo<LogoOption[]>(() => {
    const toOption = (entry: DirectoryEntry): LogoOption => ({
      id: entry.externalId,
      name: entry.name,
      logoUrl: entry.logoUrl,
      canBeContacted: entry.canBeContacted,
    });
    const byName = (a: LogoOption, b: LogoOption) =>
      a.name.localeCompare(b.name, "sv");

    /** The chosen asset type, or null for "no narrowing". */
    const assetType =
      assetTypeSetting && assetTypeSetting !== VALID_ASSET_TYPES.ALL
        ? assetTypeSetting
        : null;

    // "Alla fondbolag" with no asset-type narrowing means exactly that: every
    // contactable issuer, joined against nothing. Requiring an FA fund here
    // conflated "is a fund company we can email" with "issues something in
    // our security universe", which are unrelated — and the second is not a
    // condition the label implies.
    if (holdingsSetting === HOLDINGS_SETTINGS.ALL && !assetType) {
      return directory.map(toOption).sort(byName);
    }

    // Narrowing still needs FA, since sector codes and holdings only exist
    // there. A fundless issuer therefore drops out of an asset-type query
    // rather than being claimed to work in a sector we have no data for.
    const relevantIds = assetType
      ? idsByAssetType.get(assetType) ?? new Set<string>()
      : new Set(
          Array.from(idsByAssetType.values()).flatMap((ids) => Array.from(ids))
        );

    return directory
      .filter((entry) => relevantIds.has(entry.externalId))
      .map(toOption)
      .sort(byName);
  }, [assetTypeSetting, holdingsSetting, idsByAssetType, directory]);

  return {
    data,
    /**
     * Whether the directory holds anything at all, before relevance filtering.
     *
     * An empty `data` has two very different causes: the CMS gave us no issuers
     * (outage, empty response), or the user's holdings/asset-type filter
     * matched none of the ones it did give us. The picker needs to tell those
     * apart to avoid blaming the filter for missing upstream data.
     */
    hasIssuers: directory.length > 0,
    /**
     * True until every source the current answer depends on has data, so the
     * picker can wait and show the full result at once instead of filling in
     * source by source.
     *
     * "Has data" rather than Apollo's `loading`: with cache-and-network that is
     * also true while a cached answer is being refreshed, and a picker already
     * holding the cached answer has no reason to hide it.
     */
    loading:
      directoryLoading ||
      ((holdingsSetting === HOLDINGS_SETTINGS.PORTFOLIO ||
        (assetTypeSetting && assetTypeSetting !== VALID_ASSET_TYPES.ALL)) &&
        allSecuritiesLoading &&
        !allSecuritiesData) ||
      (holdingsSetting === HOLDINGS_SETTINGS.PORTFOLIO &&
        portfolioHoldingsLoading &&
        !portfolioHoldingsData),
    error: directoryError || allSecuritiesError || portfolioHoldingsError,
  };
};

/**
 * Starts every query `useRelevantIssuers` reads, without using the answers.
 *
 * Mounted next to the always-visible "Kontakta fondbolag" button so the data
 * is usually cached before anyone opens the modal, which then opens complete.
 * All three are shared queries (Innehav, Fondlistan, Fondbolag read the same
 * ones), so this mostly warms what another page would have fetched anyway.
 */
export const usePrefetchRelevantIssuers = () => {
  useGetAllPortfoliosHoldings();
  useGetSecurities();
  useIssuerDirectory();
};
